import type { Types } from '@ohif/core';
import { utils } from '@ohif/core';
import { findSeriesId, getSeriesArchive } from './clients/orthanc';
import { describeError } from './utils';

const { downloadBlob } = utils;

/**
 * D12. This extension's first getCommandsModule. defaultContext: 'DEFAULT'
 * matches the sibling thumbnail-menu commands (openDICOMTagViewer,
 * addDisplaySetAsLayer) in extensions/default/src/commandsModule.ts. It is a
 * convention, not a constraint: a bare-string `commands` carries no context,
 * so CommandsManager.getCommand falls back to searching contextOrder.
 */
export default function getCommandsModule({
  servicesManager,
}: Types.Extensions.ExtensionParams): Types.Extensions.CommandsModule {
  const actions = {
    downloadSeries: async ({ displaySetInstanceUID }: { displaySetInstanceUID: string }) => {
      // platform/core declares displaySetService optional on services; same
      // cast SeriesLabelService.ts uses for the same reason.
      const displaySetService = servicesManager.services
        .displaySetService as AppTypes.DisplaySetService;
      const { uiNotificationService } = servicesManager.services;

      const notifyFailure = (message: string) =>
        uiNotificationService?.show({
          title: 'Download series',
          message,
          type: 'error',
          duration: 5000,
        });

      // getDisplaySetByUID THROWS on a non-string uid and returns undefined on
      // a cache miss, while declaring `: DisplaySet` -- neither guard is implied
      // by the types, and this runs inside an un-awaited runAsync, so an escaped
      // throw is an unhandled rejection rather than a visible error.
      if (typeof displaySetInstanceUID !== 'string') {
        notifyFailure('Could not identify this series.');
        return;
      }
      const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);

      // D14 applies to every branch, not just the findSeriesId null: an
      // explicit user click that does nothing looks broken.
      const { SeriesInstanceUID } = displaySet ?? {};
      if (!SeriesInstanceUID) {
        notifyFailure('Could not identify this series.');
        return;
      }

      let orthancSeriesId: string | null;
      try {
        orthancSeriesId = await findSeriesId(SeriesInstanceUID);
      } catch (error) {
        console.error(
          `[sycai] findSeriesId failed for ${SeriesInstanceUID} ${describeError(error)}`
        );
        orthancSeriesId = null;
      }

      // D14. A null lookup shows a toast -- the only in-repo precedent for a
      // null Orthanc lookup (SeriesLabelService/QACheckItem) fails silently,
      // which is correct for a passive background readout but would make an
      // explicit user click look broken.
      if (!orthancSeriesId) {
        notifyFailure('Could not find this series on the PACS.');
        return;
      }

      let archive: Blob;
      try {
        archive = await getSeriesArchive(orthancSeriesId);
      } catch (error) {
        console.error(
          `[sycai] getSeriesArchive failed for ${orthancSeriesId} ${describeError(error)}`
        );
        notifyFailure('Could not download this series.');
        return;
      }

      // D11. utils.downloadBlob rather than a hand-rolled anchor/createObjectURL
      // dance -- does the identical sequence develop wrote by hand.
      downloadBlob(archive, { filename: `${SeriesInstanceUID}.zip` });
    },
  };

  const definitions = {
    downloadSeries: actions.downloadSeries,
  };

  return {
    actions,
    definitions,
    defaultContext: 'DEFAULT',
  };
}
